import { useCallback, useEffect, useMemo, useState } from 'react';

import { tracking } from '@iwoca/frontend-tracking-library';

import { evaluate } from './logic/outcomes.config';
import { QUESTIONS, TOTAL } from './logic/questions.config';
import { answeredCount } from './logic/scoring';
import { trackingValue } from './logic/trackingValue';
import { TAnswers, TAnswerValue } from './logic/types';
import { getAmplitudeDefaultEventProperties } from '../../utils/amplitudeEvents';

type TPhase = 'intro' | 'question' | 'result';

export const useCreditCheck = ({ trackingEnabled = true } = {}) => {
  const [phase, setPhase] = useState<TPhase>('intro');
  const [index, setIndex] = useState(0);
  const [answers, setAnswers] = useState<TAnswers>({});

  const currentQuestion = phase === 'question' ? QUESTIONS[index] : undefined;
  const result = useMemo(() => evaluate(answers), [answers]);

  useEffect(() => {
    // Mirrors the pageLoaded auth gate so redirected logged-in users don't record phantom step loads.
    if (!trackingEnabled) return;
    if (phase === 'question') {
      const question = QUESTIONS[index];
      tracking.ampli.creditCheckStepLoaded({
        step: 'question',
        question_id: question.id,
        question_index: index + 1,
        ...getAmplitudeDefaultEventProperties(),
      });
    } else {
      tracking.ampli.creditCheckStepLoaded({
        step: phase,
        ...getAmplitudeDefaultEventProperties(),
      });
    }
  }, [phase, index, trackingEnabled]);

  const start = useCallback(() => {
    tracking.ampli.creditCheckStarted();
    setPhase('question');
    setIndex(0);
  }, []);

  const setAnswer = useCallback((id: string, value: TAnswerValue) => {
    setAnswers((prev) => ({ ...prev, [id]: value }));
  }, []);

  const next = useCallback(() => {
    const question = QUESTIONS[index];
    const value = trackingValue(question.id, answers[question.id]);
    tracking.ampli.creditCheckQuestionAnswered({
      question_id: question.id,
      question_index: index + 1,
      // Next without a value is a skip, not an answer — keeps 'answered' meaningful in Amplitude.
      ...(value === undefined
        ? { action: 'skipped' }
        : { action: 'answered', value }),
      ...getAmplitudeDefaultEventProperties(),
    });
    if (index >= TOTAL - 1) {
      setPhase('result');
    } else {
      setIndex(index + 1);
    }
  }, [index, answers]);

  const skip = useCallback(() => {
    const question = QUESTIONS[index];
    tracking.ampli.creditCheckQuestionAnswered({
      question_id: question.id,
      question_index: index + 1,
      action: 'skipped',
      ...getAmplitudeDefaultEventProperties(),
    });
    setAnswers((prev) => ({ ...prev, [question.id]: undefined }));
    if (index >= TOTAL - 1) {
      setPhase('result');
    } else {
      setIndex(index + 1);
    }
  }, [index]);

  const back = useCallback(() => {
    tracking.ampli.creditCheckBackClicked({
      from_question_index: index + 1,
      to_phase: index > 0 ? 'question' : 'intro',
      ...getAmplitudeDefaultEventProperties(),
    });
    if (index > 0) setIndex(index - 1);
    else setPhase('intro');
  }, [index]);

  const restart = useCallback(() => {
    tracking.ampli.creditCheckRestarted();
    setAnswers({});
    setIndex(0);
    setPhase('intro');
  }, []);

  useEffect(() => {
    if (phase !== 'result') return;
    const answered = answeredCount(answers);
    tracking.ampli.creditCheckCompleted({
      credit_check_outcome: result.key,
      meter: result.meter,
      answered_count: answered,
      skipped_count: TOTAL - answered,
      ...getAmplitudeDefaultEventProperties(),
    });
  }, [phase, result, answers]);

  return {
    phase,
    index,
    answers,
    currentQuestion,
    result,
    start,
    setAnswer,
    next,
    skip,
    back,
    restart,
  };
};
